import { Fragment } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { User } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import type { User as SupabaseUser } from '@supabase/supabase-js'
import {
  buildAuthHref,
  getAccountActions,
  resolveDisplayName,
} from './auth-menu-actions'

interface AuthButtonProps {
  user: SupabaseUser | null
  isAdmin: boolean
  displayName?: string | null
}

export function AuthButton({ user, isAdmin, displayName }: AuthButtonProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const resolvedDisplayName = resolveDisplayName(displayName, user?.email)
  const accountActions = getAccountActions(isAdmin)

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/')
  }

  if (!user) {
    return (
      <Link to={buildAuthHref(location.pathname, searchParams, 'signin')}>
        <Button variant="outline" size="sm">
          Sign In
        </Button>
      </Link>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <User className="h-4 w-4 mr-2" />
          <span className="hidden sm:inline max-w-32 truncate">
            {resolvedDisplayName}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {accountActions.map(action => (
          <Fragment key={action.key}>
            {action.separatorBefore && <DropdownMenuSeparator />}
            {action.actionType === 'navigate' ? (
              <DropdownMenuItem asChild>
                <Link to={action.href} className="cursor-pointer">
                  <action.icon className="mr-2 h-4 w-4" />
                  {action.label}
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                onClick={handleLogout}
                className={action.destructive ? 'cursor-pointer text-red-600' : 'cursor-pointer'}
              >
                <action.icon className="mr-2 h-4 w-4" />
                {action.label}
              </DropdownMenuItem>
            )}
            {action.separatorAfter && <DropdownMenuSeparator />}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
